import {useCallback, useMemo, useRef} from 'react'
import {GearIcon} from '@primer/octicons-react'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {pullRequestsOverviewRoute} from '../../routes/overview-route'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import type {ProjectPickerBaseProject} from '@github-ui/item-picker-project'
import {useModifyProjects} from '../../mutations/use-modify-projects'
import {useProjectsQuery} from './data/route-queries'
import {SidebarDisclosureHeader} from './SidebarDisclosureHeader'
import {MutationFailureBanner} from '../FailureBanner'

const EMPTY_PROJECTS: ProjectPickerBaseProject[] = []

export function ProjectsPicker() {
  const {data, isPending, isError} = useProjectsQuery()
  const {owner, repo} = useRouteParams(pullRequestsOverviewRoute)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const modifyProjectsMutation = useModifyProjects()
  const {mutate, isPending: isMutationPending} = modifyProjectsMutation

  // Memoized so the picker receives stable array identities; a fresh array on every
  // render would re-sort the open list and clobber in-progress selections.
  const selectedProjects: ProjectPickerBaseProject[] = useMemo(
    () =>
      (data?.projects ?? EMPTY_PROJECTS).map(project => ({
        id: project.id,
        title: project.title,
        closed: project.closed,
        number: project.number,
        url: project.url,
        viewerCanUpdate: project.viewerCanUpdate,
        hasReachedItemsLimit: false,
      })),
    [data?.projects],
  )

  // The picker keys removal by project id, but the mutation needs this pull request's
  // specific project *item* id for that project -- look it up from the current data.
  const itemIdByProjectId = useMemo(
    () => new Map((data?.projects ?? []).map(project => [project.id, project.itemId])),
    [data?.projects],
  )

  const handleSave = useCallback(
    (nextProjects: ProjectPickerBaseProject[]) => {
      const previousIds = new Set(selectedProjects.map(project => project.id))
      const nextIds = new Set(nextProjects.map(project => project.id))

      const addProjects = nextProjects.filter(project => !previousIds.has(project.id)).map(project => project.id)
      const removeItems = selectedProjects
        .filter(project => !nextIds.has(project.id))
        .map(project => itemIdByProjectId.get(project.id))
        .filter((itemId): itemId is string => Boolean(itemId))

      if (addProjects.length > 0 || removeItems.length > 0) {
        mutate({addProjects, removeItems, nextProjects})
      }
    },
    [selectedProjects, itemIdByProjectId, mutate],
  )

  if (isPending && !data) return <ProjectsHeader disabled aria-label="Loading projects" />
  if (isError && !data) return <ProjectsHeader disabled aria-label="Projects couldn't be loaded" />
  if (owner === undefined || repo === undefined) return null

  return (
    <>
      <ProjectPicker
        pickerId="pr-overview-projects-picker"
        repo={repo}
        owner={owner}
        selectedProjects={selectedProjects}
        isLoading={isMutationPending}
        onSave={handleSave}
        anchorElement={(anchorProps, ref) => (
          <ProjectsHeader ref={ref ?? anchorRef} aria-label="Manage projects" {...anchorProps} />
        )}
      />
      <MutationFailureBanner
        mutation={modifyProjectsMutation}
        title="Failed to update projects"
        returnFocusRef={anchorRef}
        className="mt-2"
      />
    </>
  )
}

function ProjectsHeader(props: React.ComponentPropsWithRef<'button'>) {
  return <SidebarDisclosureHeader title="Projects" trailingIcon={GearIcon} aria-label="Manage projects" {...props} />
}
